import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";

export interface BreadcrumbItem {
  name: string;
  href: string;
  state?: string;
}

interface BreadcrumbContextType {
  trail: BreadcrumbItem[];
  updateTrail: (
    name: string,
    href: string,
    state?: string,
    isRoot?: boolean,
  ) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(
  undefined,
);

export const BreadcrumbProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [trail, setTrail] = useState<BreadcrumbItem[]>(() => {
    const savedTrail = sessionStorage.getItem("breadcrumbTrail");
    return savedTrail ? JSON.parse(savedTrail) : [];
  });

  const updateTrail = useCallback(
    (name: string, href: string, state?: string, isRoot: boolean = false) => {
      setTrail((prev) => {
        let newTrail: BreadcrumbItem[];

        if (isRoot) {
          newTrail = [{ name, href, state }];
        } else {
          const existingIndex = prev.findIndex((item) => item.href === href);

          if (existingIndex !== -1) {
            newTrail = prev.slice(0, existingIndex + 1);
            newTrail[newTrail.length - 1].name = name;
            newTrail[newTrail.length - 1].state = state;
          } else {
            const currentPathname = href.split("?")[0];
            const lastItem = prev[prev.length - 1];

            if (lastItem && lastItem.href.split("?")[0] === currentPathname) {
              newTrail = [...prev];
              newTrail[newTrail.length - 1] = { name, href, state };
            } else {
              newTrail = [...prev, { name, href, state }];
            }
          }
        }

        sessionStorage.setItem("breadcrumbTrail", JSON.stringify(newTrail));
        return newTrail;
      });
    },
    [],
  );

  return (
    <BreadcrumbContext.Provider value={{ trail, updateTrail }}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

export const useBreadcrumb = (): BreadcrumbContextType => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error("useBreadcrumb must be used within a BreadcrumbProvider");
  }
  return context;
};
